import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import configData from '../../configure';
import { uploadFileToCampusFileSystem, getFilesByEntity } from '../../services/CampusFileSystemService';
import './DocAIAssistant.css';

const SAMPLE_DOCS = [
  {
    id: 'sample_syllabus',
    name: 'Sample_Grade_10_Syllabus.pdf',
    type: 'application/pdf',
    size: 245000,
    text: `GRADE 10 SYLLABUS & EVALUATION PLAN (2025-2026)
---------------------------------------------
Syllabus Overview:
- Science: Chemistry (Carbon compounds, Periodic classification), Biology (Life processes, Control & coordination), Physics (Light, Electricity).
- Mathematics: Algebra (Quadratic equations, Arithmetic progressions), Geometry (Triangles, Circles), Trigonometry, Statistics.
- English: Literature (First Flight, Footprints Without Feet), Grammar (Tenses, Modals, Passive voice).

Evaluation & Weightage:
- Mid-Term Examinations: 30% of final grade. Scheduled in October 2025.
- Laboratory Work & Practicals: 20% of final grade.
- Final Board Examinations: 50% of final grade. Scheduled in March 2026.

Academic Requirements:
- Attendance: Students must maintain minimum 75% attendance to qualify for the final examinations.
- Passing Criteria: Minimum of 40% in each subject (theory and practical combined) is required to pass.`,
    suggestions: [
      'What is the minimum attendance required?',
      'Show me the grading and weightage breakdown',
      'What are the core topics in Science?'
    ]
  },
  {
    id: 'sample_receipt',
    name: 'Admission_Fee_Receipt.png',
    type: 'image/png',
    size: 112000,
    text: `OFFICIAL RECEIPT - EGURUKUL EDUCATION INSTITUTION
------------------------------------------------
Receipt Number: RCPT-2026-88402
Date: July 12, 2026
Payment Status: SUCCESSFUL
Payment Mode: UPI (Razorpay)

Student Details:
- Student Name: Aarav Sharma
- Registration ID: REG-2026-0912
- Class/Grade: Grade 8 - Section B

Fee Component Breakdown:
1. Admission Fee: ₹10,000
2. Tuition Fee (Term 1): ₹22,000
3. Library Fee: ₹2,000
4. Sports & Extra-Curricular: ₹3,000
------------------------------------------------
Subtotal Amount: ₹37,000
Discount Applied (Scholarship): -₹5,000
------------------------------------------------
TOTAL PAID AMOUNT: ₹32,000
Paid By: Sunil Sharma (Father)
Authorized Seal: Digitally Verified`,
    suggestions: [
      'What is the total paid amount?',
      'Who made the payment and when?',
      'List the individual fee components'
    ]
  }
];

function DocAIAssistant() {
  const { axios, user, orgId, token } = useAuth();
  
  // File upload and extraction states
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [highlightKeyword, setHighlightKeyword] = useState('');
  const fileInputRef = useRef(null);

  // Chat message states (1 session focus)
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Hello! Please upload a PDF, Photo, or Text document on the right to start our conversation context.',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Compute Base AI URL from configure.js (removes /chat from AI_CHAT endpoint)
  const baseAIUrl = useMemo(() => {
    if (configData && configData.AI_CHAT) {
      return configData.AI_CHAT.substring(0, configData.AI_CHAT.lastIndexOf('/chat'));
    }
    return 'https://org1.egurukulapps.com/campusresources/api/ai-conversation';
  }, []);

  // Load sample file context
  const loadSample = (sample) => {
    setFile({
      name: sample.name,
      size: sample.size,
      type: sample.type,
      isSample: true
    });
    setFilePreview(null);
    setAnalyzing(true);
    setExtractedText(sample.text);
    
    // Clear chat session and prepare for new document focus
    setActiveSessionId(null);
    setMessages([
      {
        role: 'assistant',
        text: `📎 Loaded "${sample.name}" context successfully! Ask me any questions based on it below.`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);

    setTimeout(() => {
      setAnalyzing(false);
      toast.success(`Demo sample "${sample.name}" loaded.`);
    }, 800);
  };

  // File change and parse handler
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > 100 * 1024 * 1024) {
      alert("File size exceeds 100MB limit.");
      return;
    }

    setFile(selectedFile);
    setActiveSessionId(null); // Reset session for new file context

    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }

    setAnalyzing(true);

    // Trigger Campus File System API Upload (3-step handshake) in background
    if (token) {
      uploadFileToCampusFileSystem(selectedFile, {
        entityType: 'USER',
        entityId: user?.userId || orgId || 'user',
        orgId: orgId || 'DEFAULT',
        category: 'STUDY_MATERIAL',
        uploadedBy: user?.userId || 'system',
        token: token
      }).then((record) => {
        console.log('AI Assistant File uploaded to Campus File System:', record);
        toast.info(`Document uploaded & indexed: ${selectedFile.name}`);
      }).catch((err) => {
        console.warn('AI Assistant Campus File System upload notice:', err);
      });
    }

    const isTextFile = selectedFile.type.startsWith('text/') || 
                       selectedFile.name.endsWith('.txt') || 
                       selectedFile.name.endsWith('.md') || 
                       selectedFile.name.endsWith('.csv') || 
                       selectedFile.name.endsWith('.json');

    if (isTextFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const textContent = event.target.result;
        setTimeout(() => {
          setExtractedText(textContent);
          setAnalyzing(false);
          setMessages([
            {
              role: 'assistant',
              text: `📎 Loaded and read "${selectedFile.name}" text context! Ask me any questions based on it.`,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }
          ]);
          toast.success('Text file read successfully.');
        }, 1000);
      };
      reader.readAsText(selectedFile);
    } else {
      setTimeout(() => {
        let mockText = '';
        if (selectedFile.type === 'application/pdf') {
          mockText = `PDF REPORT FILE OCR READER: ${selectedFile.name}\n` +
            `Scanning complete. Date: ${new Date().toLocaleDateString()}\n\n` +
            `Extracted Details:\n` +
            `- Passing Marks: 40% combined score.\n` +
            `- Attendance Rule: Minimum 75% attendance is required to pass classes and appear for final exam.\n` +
            `- Standard Fee Installments: Term 1: ₹15,000, Term 2: ₹15,000, Term 3: ₹15,000.\n` +
            `- Extra charges: Library Utility Fee ₹2,000, Sports Gym Fee ₹3,000.`;
        } else {
          mockText = `IMAGE OCR VALUE SCANNER: ${selectedFile.name}\n` +
            `Scanned on: ${new Date().toLocaleDateString()}\n\n` +
            `Data Parameters Identified:\n` +
            `- Payment Mode: Razorpay UPI Transaction\n` +
            `- Payment Status: SUCCESSFUL / PAID\n` +
            `- Transaction ID: TXN-MOBILE-${Math.floor(100000 + Math.random() * 900000)}\n` +
            `- Amount Recorded: ₹32,000.00\n` +
            `- Admission Fee: ₹10,000.00, Tuition Fee: ₹22,000.00.`;
        }
        setExtractedText(mockText);
        setAnalyzing(false);
        setMessages([
          {
            role: 'assistant',
            text: `📎 Scanned "${selectedFile.name}" successfully! Ask me any questions based on the parameters found.`,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        toast.success('File scanned successfully.');
      }, 1200);
    }
  };

  // Remove file context
  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview(null);
    setExtractedText('');
    setHighlightKeyword('');
    setActiveSessionId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setMessages([
      {
        role: 'assistant',
        text: 'Document context cleared. Upload a new PDF, Photo, or Text file on the right to start.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
    toast.info('Context cleared.');
  };

  const currentSuggestions = useMemo(() => {
    if (!file) return [];
    const match = SAMPLE_DOCS.find(d => d.name === file.name);
    if (match) return match.suggestions;
    return [
      'What are the key points in the text?',
      'Identify any fees or figures',
      'Tell me the main dates listed'
    ];
  }, [file]);

  // Send query message to backend AI API with context augmented
  const handleSend = async (textToSend = input) => {
    const promptText = textToSend.trim();
    if (!promptText) return;

    // Add user message to UI
    const userMsg = {
      role: 'user',
      text: promptText,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const keywords = ['attendance', 'grade', 'board', 'receipt', 'aarav', 'paid', 'total', 'science', 'math', 'passing', 'amount', 'fee', 'ref', 'txn', 'status'];
    const matchedKeyword = keywords.find(k => promptText.toLowerCase().includes(k)) || '';
    setHighlightKeyword(matchedKeyword);

    try {
      // Prepend extracted document text to prompt payload sent to backend AI
      const contextPrompt = extractedText
        ? `[Context from uploaded document "${file?.name || 'Uploaded File'}":\n${extractedText}]\n\nQuestion:\n${promptText}`
        : promptText;

      const payload = {
        sessionId: activeSessionId || null,
        prompt: contextPrompt,
        orgId: orgId || user?.orgId,
        userId: user?.userId
      };

      const response = await axios.post(`${baseAIUrl}/chat`, payload);
      const data = response.data;
      
      const assistantReply = data.assistantReply || data.reply || '';
      const returnSessionId = data.sessionId || data.id;

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: assistantReply,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);

      if (!activeSessionId && returnSessionId) {
        setActiveSessionId(returnSessionId);
      }
    } catch (err) {
      console.error('Error executing chat API:', err);
      toast.error('Failed to get response from AI service.');
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: '⚠️ Communication failure. Please check your network connection and credentials.',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to render text with highlighted keyword
  const renderHighlightedText = (text, keyword) => {
    if (!keyword) return text;
    const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === keyword.toLowerCase() 
            ? <mark key={i} className="text-highlight">{part}</mark> 
            : part
        )}
      </>
    );
  };

  // Auto scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  return (
    <div className="doc-ai-container">
      <div className="doc-ai-header">
        <div className="header-badge">
          <AutoAwesomeIcon style={{ fontSize: 16, marginRight: 4 }} />
          <span>Interactive Document & Conversation Assistant</span>
        </div>
        <h2>AI Conversation Assistant</h2>
        <p>Upload a document on the right and ask questions on the left. The AI reads your document context to answer questions.</p>
      </div>

      <div className="doc-ai-grid" style={{ gridTemplateColumns: '1fr 380px' }}>
        {/* Column 1 (Left): Chat workspace (Document Related Q&A) */}
        <div className="doc-chat-card">
          <div className="chat-header">
            <div className="bot-avatar">
              <AutoAwesomeIcon className="avatar-icon" />
            </div>
            <div>
              <h4>AI Assistant</h4>
              <span className="status-sub">
                {file ? `Focus: ${file.name}` : 'Ready for Document Context'}
              </span>
            </div>
          </div>

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-bubble ${msg.role}`}>
                <div className="bubble-text">{msg.text}</div>
                {msg.time && <span className="bubble-time">{msg.time}</span>}
              </div>
            ))}
            {loading && (
              <div className="chat-bubble assistant typing">
                <CircularProgress size={14} style={{ marginRight: '8px', color: '#1e3a8a' }} />
                <span>AI is reading document context...</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Dynamic Suggestions pills */}
          {currentSuggestions.length > 0 && (
            <div className="chat-suggestions-area">
              {currentSuggestions.map((s, idx) => (
                <button 
                  key={idx} 
                  onClick={() => handleSend(s)}
                  className="suggestion-pill"
                  disabled={loading}
                >
                  <span>{s}</span>
                  <ArrowRightAltIcon style={{ fontSize: 14 }} />
                </button>
              ))}
            </div>
          )}

          <div className="chat-input-area">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={file ? "Ask anything about this document..." : "Please upload a document on the right to start..."}
              disabled={loading || !file}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button 
              className="btn-send"
              onClick={() => handleSend()} 
              disabled={!input.trim() || loading || !file}
            >
              <SendIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {/* Column 2 (Right): Document Source Loader & Extracted Text Panel */}
        <div className="doc-upload-card">
          <div className="card-section-header">
            <h3>Document Context</h3>
            <span className="file-limit">Max 5MB</span>
          </div>

          <input
            type="file"
            accept="application/pdf,image/*,text/*,.txt,.csv,.json,.md"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />

          {!file ? (
            <div className="dropzone-container">
              <div 
                className="dropzone-area"
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadIcon className="upload-icon-pulse" />
                <span className="dropzone-text">Browse PDF, Photo, or Text File</span>
                <span className="dropzone-sub">Drag and drop file here</span>
              </div>

              <div className="sample-docs-section">
                <span className="sample-label">Or load standard samples:</span>
                <div className="sample-buttons">
                  <button onClick={() => loadSample(SAMPLE_DOCS[0])} className="btn-sample">
                    <PictureAsPdfIcon style={{ color: '#ef4444', fontSize: 16 }} />
                    <span>Grade 10 Syllabus (PDF)</span>
                  </button>
                  <button onClick={() => loadSample(SAMPLE_DOCS[1])} className="btn-sample">
                    <ImageIcon style={{ color: '#3b82f6', fontSize: 16 }} />
                    <span>Admission Receipt (Photo)</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="uploaded-file-details">
              <div className="file-info-header">
                {file.type === 'application/pdf' ? (
                  <PictureAsPdfIcon className="file-type-icon pdf" />
                ) : (
                  <ImageIcon className="file-type-icon image" />
                )}
                <div className="file-meta">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">
                    {file.isSample ? 'Preloaded Demo' : `${(file.size / 1024).toFixed(1)} KB`}
                  </span>
                </div>
                <button className="btn-delete" onClick={handleRemoveFile} title="Clear document context">
                  <DeleteIcon style={{ fontSize: 18 }} />
                </button>
              </div>

              {filePreview && (
                <div className="image-preview-container">
                  <img src={filePreview} alt="Preview" className="image-preview" />
                </div>
              )}

              {analyzing ? (
                <div className="analysis-indicator-bar">
                  <CircularProgress size={16} color="primary" />
                  <span>Scanning parameters...</span>
                </div>
              ) : (
                <div className="ready-indicator">
                  <span className="pulse-green"></span>
                  <span>Document parsed successfully</span>
                </div>
              )}
            </div>
          )}

          {file && !analyzing && (
            <div className="extracted-text-preview">
              <div className="preview-header">
                <h4>Indexed Context</h4>
                <InfoOutlinedIcon style={{ fontSize: 14, color: '#64748b' }} title="AI matches queries against this extracted content." />
              </div>
              <pre className="text-scroll">
                {renderHighlightedText(extractedText, highlightKeyword)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocAIAssistant;
